import dayjs from "dayjs"

import {
  PRODUCT_TEST_DOCUMENT_TYPE_TO_COLOR,
  PRODUCT_TEST_DOCUMENT_TYPE_TO_LABEL,
  PRODUCT_TEST_RESULT_PASS_FAIL_TO_LABEL,
  PRODUCT_TEST_RESULT_YES_NO_TO_LABEL,
  PRODUCT_TEST_STATUS_TO_LABEL,
} from "./constants"
import type { ProductTestFormValues } from "./schemas"
import type {
  ProductTestFormPayload,
  ProductTestResultPassFail,
  ProductTestResultYesNo,
  ProductTestWithDocuments,
} from "./types"
import { ProductTestDiffType } from "./types"

import { DATE_FORMAT } from "@/constants/formats"
import type {
  AssociatedTestTemplate,
  FieldGroupEnum,
  ProductTest,
  ProductTestStatus,
  SignedUrl,
} from "@/graphql/codegen/graphql"
import colors from "@/styles/colors"
import { toNumberOrNull, toStringOrEmpty, toStringOrNull } from "@/utils/forms"
import { calculateRemainingShelfLife } from "@/utils/product"

export const calculateExpirationDate = (manufacturingDate: string, shelfLifeMonths: number): string =>
  dayjs(manufacturingDate).add(shelfLifeMonths, "month").format(DATE_FORMAT.ISO_18601)

export const getProductTestStatusLabel = (status?: ProductTestStatus | null): string => {
  if (!status) return ""

  return PRODUCT_TEST_STATUS_TO_LABEL.get(status) ?? status
}

export const getProductTestDocumentTypeLabel = (documentType?: FieldGroupEnum | null): string => {
  if (!documentType) return ""

  return PRODUCT_TEST_DOCUMENT_TYPE_TO_LABEL[documentType] ?? documentType
}

export const getProductTestDocumentTypeColor = (documentType?: FieldGroupEnum | null): string => {
  if (!documentType) return "transparent"

  return PRODUCT_TEST_DOCUMENT_TYPE_TO_COLOR[documentType] ?? documentType
}

export const getProductTestResultPassFailLabel = (value?: ProductTestResultPassFail | null): string => {
  if (!value) return ""

  return PRODUCT_TEST_RESULT_PASS_FAIL_TO_LABEL.get(value) ?? value
}

export const getProductTestResultYesNoLabel = (value?: ProductTestResultYesNo | null): string => {
  if (!value) return ""

  return PRODUCT_TEST_RESULT_YES_NO_TO_LABEL.get(value) ?? value
}

export const getProductTestResultShelfLifeColor = (manufacturingDate: string, expirationDate: string): string => {
  const remainingShelfLife = calculateRemainingShelfLife(manufacturingDate, expirationDate)!

  if (remainingShelfLife === 0) {
    return colors.secondary["100"] // Red: Expired or no data
  } else if (remainingShelfLife <= 50) {
    return colors.tertiary.yellow["100"] // Amber: ≤ 50% and > 0%
  } else {
    return colors.tertiary.green["100"] // Green: > 50%
  }
}

export const getProductTestDefaultFormValues = (
  associatedTestTemplates: AssociatedTestTemplate[],
  productTest?: ProductTestWithDocuments | null
): ProductTestFormValues => {
  const defaultResults = associatedTestTemplates.map((associatedTemplate) => ({
    associatedTestTemplateId: associatedTemplate.associatedTestTemplateId,
    value: "",
    printToCoa: associatedTemplate.printToAkrochem,
  }))

  const lotNumber = productTest ? [productTest?.lotNumber, productTest?.version].filter(Boolean).join(" ").trim() : ""

  return {
    lotNumber,
    batchNumber: toStringOrEmpty(productTest?.batchNumber),
    orderId: "",
    manufacturingDate: toStringOrEmpty(productTest?.manufacturingDate),
    expirationDate: toStringOrEmpty(productTest?.expirationDate),
    status: toStringOrEmpty(productTest?.status),
    results:
      productTest?.results?.map((result) => ({
        productTestResultId: result.productTestResultId,
        associatedTestTemplateId: result.associatedTestTemplate!.associatedTestTemplateId,
        value: toStringOrEmpty(result.value),
        printToCoa: result.printToCoa!,
      })) ?? defaultResults,
    comment: "",
    documents: [],
  }
}

export const compareProductTestValues = (
  productTest: ProductTest,
  formValues: ProductTestFormValues
): ProductTestDiffType | null => {
  if (productTest.results?.some((result, index) => result.value !== formValues.results[index].value)) {
    return ProductTestDiffType.TestResults
  }

  if (productTest.expirationDate !== formValues.expirationDate) {
    return ProductTestDiffType.ExpirationDate
  }

  return null
}

export const hasProductTestDocumentsChanged = (
  existingDocuments: SignedUrl[],
  currentDocuments: SignedUrl[]
): boolean => {
  if (existingDocuments.length !== currentDocuments.length) return true

  return !existingDocuments.every((existingDocument) =>
    currentDocuments.some((currentDocument) => existingDocument.file?.fileId === currentDocument.file?.fileId)
  )
}

export const hasProductTestPoIdsChanged = (existingPoIds: number[], currentPoIds: number[]): boolean => {
  if (existingPoIds.length !== currentPoIds.length) return true
  return !existingPoIds.every((poId) => currentPoIds.includes(poId))
}

export const toProductTestFormPayload = (
  formValues: ProductTestFormValues,
  productTest?: ProductTestWithDocuments | null,
  remainingDocuments: SignedUrl[] = [],
  productTestPoIds: number[] = [],
  hasResultsChanged = false
): ProductTestFormPayload => {
  const documentsToUpload = formValues.documents.filter(({ file }) => file)
  let documentsToDelete: string[] = []

  if (productTest) {
    const remainingFileIds = new Set(remainingDocuments.map((doc) => doc.file?.fileId).filter(Boolean))
    documentsToDelete = productTest.documents
      .filter((document) => !remainingFileIds.has(document.file?.fileId))
      .map((document) => document.file?.fileId ?? "")
      .filter(Boolean)
  }

  // Convert single order ID string to array with one number (for now, hardcoded to use single order ID)
  const orderId = toNumberOrNull(formValues.orderId)
  const isNewOrderIdAdded = !productTestPoIds.includes(orderId!)
  const orderIds = isNewOrderIdAdded && orderId ? [orderId].concat(productTestPoIds) : productTestPoIds

  return {
    productTest: {
      lotNumber: formValues.lotNumber,
      batchNumber: toStringOrNull(formValues.batchNumber),
      orderIds,
      manufacturingDate: formValues.manufacturingDate,
      expirationDate: formValues.expirationDate,
      status: toStringOrNull(formValues.status) as ProductTestStatus | null,
      // Send results only if they have changed in the form
      results: hasResultsChanged ? formValues.results : null,
      comment: toStringOrNull(formValues.comment),
    },
    documentsToUpload,
    documentsToDelete,
    documentsToCopy: remainingDocuments,
  }
}

export const isTestResultsValid = (
  { equalToValue, minimumValue, maximumValue }: AssociatedTestTemplate,
  testResultsValue: number
): boolean => {
  return !(
    (minimumValue && testResultsValue < +minimumValue) ||
    (maximumValue && testResultsValue > +maximumValue) ||
    (equalToValue && testResultsValue !== +equalToValue)
  )
}
